import { AssetSubClassCode, Entity, SnakeMeta } from '@api';
import { TabLines } from '@design/components/tab-lines/TabLines';
import { TabsType } from '@terminal/modules/tab-content/types';
import { useCompanyTheme } from '@terminal/react-query-hooks/knowledge-graph/useKnowledgeGraph';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import { EntityHeader } from '../entity-header/EntityHeader';
import { ChartPrice } from '../simple-chart/components/chart-price/ChartPrice';
import { getTabs } from '../tab-content/utils';
import styles from './AssetOverviewHeader.module.scss';

export interface AssetOverviewHeaderProps {
  snakeMeta?: SnakeMeta;
  entity: Entity;
  activeTab: TabsType;
  changeTab: (index: number, tabKey: TabsType) => void;
  showShadow: boolean;
  documentsCount?: number;
}

export const AssetOverviewHeader = ({
  snakeMeta,
  entity,
  activeTab,
  changeTab,
  showShadow,
  documentsCount = 0,
}: AssetOverviewHeaderProps) => {
  const { t } = useTranslation([
    'chart',
    'analyze',
    'countries',
    'asset-overview',
    'earnings',
  ]);

  const { error } = useCompanyTheme({ entityTag: entity.tag });

  const is404Error = error?.status === 404;
  const isStock = entity.sub_class === AssetSubClassCode.EquityStock;
  const isEcoCentralBank =
    entity.sub_class === AssetSubClassCode.EcoCentralBank;
  const isDocumentsTabVisible =
    (isStock || isEcoCentralBank) && !!documentsCount;
  const tabs = getTabs(t, { isStock, isDocumentsTabVisible, is404Error });
  const filteredTabs = tabs.filter(tab => !tab.isHidden);

  return (
    <div
      className={clsx(styles.header, {
        [styles.showShadow]: showShadow,
      })}
    >
      <div className={styles.layout}>
        <div
          className={clsx(styles.headerContent, {
            [styles.noEntity]: !entity,
          })}
        >
          {entity && (
            <>
              <div className={styles.contentWrapper}>
                <EntityHeader
                  entity={entity}
                  priceDisplayNode={
                    <ChartPrice
                      entity={entity}
                      snakeMeta={snakeMeta}
                      enablePriceHover={false}
                    />
                  }
                />
              </div>
              <TabLines
                tabs={filteredTabs}
                activeTab={activeTab}
                onChange={changeTab}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};
